import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { db, auth, storage } from "../firebase";
import { collection, addDoc, serverTimestamp, doc, updateDoc, arrayUnion } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import styles from "../styles/CreateClub.module.css";

export default function CreateClub() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "coding",
    privacy: "public",
    themeColor: "#667eea",
    rules: "Be respectful\nNo spam\nStay on topic",
    welcomeMessage: "Welcome to our club! We're excited to have you here."
  });
  const [logoFile, setLogoFile] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewLogo, setPreviewLogo] = useState(null);
  const [previewBanner, setPreviewBanner] = useState(null);

  const categories = [
    { value: "coding", label: "💻 Coding & Tech" },
    { value: "sports", label: "⚽ Sports" },
    { value: "music", label: "🎵 Music" },
    { value: "art", label: "🎨 Art & Design" },
    { value: "gaming", label: "🎮 Gaming" },
    { value: "education", label: "📚 Education" },
    { value: "business", label: "💼 Business" },
    { value: "entertainment", label: "🎬 Entertainment" }
  ];

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    if (type === "logo") {
      setLogoFile(file);
      setPreviewLogo(URL.createObjectURL(file));
    } else {
      setBannerFile(file);
      setPreviewBanner(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!auth.currentUser) {
      alert("Please login first");
      return;
    }

    if (!formData.name.trim()) {
      alert("Club name is required");
      return;
    }

    setLoading(true);
    try {
      const user = auth.currentUser;

      // Upload logo
      let logoUrl = "";
      if (logoFile) {
        const logoRef = ref(storage, `club_logos/${Date.now()}_${logoFile.name}`);
        await uploadBytes(logoRef, logoFile);
        logoUrl = await getDownloadURL(logoRef);
      }

      // Upload banner
      let bannerUrl = "";
      if (bannerFile) {
        const bannerRef = ref(storage, `club_banners/${Date.now()}_${bannerFile.name}`);
        await uploadBytes(bannerRef, bannerFile);
        bannerUrl = await getDownloadURL(bannerRef);
      }

      // Create club document
      const clubData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        category: formData.category,
        privacy: formData.privacy,
        themeColor: formData.themeColor,
        logoUrl,
        bannerUrl,
        creatorId: user.uid,
        creatorEmail: user.email,
        creatorName: user.displayName || user.email.split('@')[0],
        coHandlers: [],
        members: [user.uid],
        memberCount: 1,
        posts: 0,
        rules: formData.rules.split('\n').filter(rule => rule.trim()),
        welcomeMessage: formData.welcomeMessage,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        recentActivity: serverTimestamp()
      };

      const clubRef = await addDoc(collection(db, "clubs"), clubData);

      // Add creator as admin member
      await addDoc(collection(db, "club_members"), {
        clubId: clubRef.id,
        userId: user.uid,
        userEmail: user.email,
        userName: user.displayName || user.email.split('@')[0],
        role: "creator",
        joinedAt: serverTimestamp(),
        status: "active",
        notifications: true
      });

      // Add club to user's created clubs
      const userRef = doc(db, "users", user.uid);
      await updateDoc(userRef, {
        createdClubs: arrayUnion(clubRef.id),
        updatedAt: serverTimestamp()
      });

      alert("Club created successfully!");
      navigate(`/club/${clubRef.id}`);
    } catch (err) {
      console.error("Error creating club:", err);
      alert("Failed to create club: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>Create New Channel</h2>
      <p className={styles.subtitle}>Create your own community channel like Instagram</p>

      <form onSubmit={handleSubmit}>
        {/* Banner Upload */}
        <div className={styles.formSection}>
          <label className={styles.label}>Channel Banner (Recommended: 1200x400px)</label>
          <div className={styles.bannerUpload}>
            <div
              className={styles.bannerPreview}
              style={{
                backgroundImage: previewBanner ? `url(${previewBanner})` : `linear-gradient(135deg, ${formData.themeColor} 0%, #aaa 100%)`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            >
              {!previewBanner && (
                <div className={styles.bannerPlaceholder}>
                  <span>Upload Banner</span>
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileChange(e, "banner")}
                className={styles.fileInput}
              />
            </div>
          </div>
        </div>

        {/* Logo Upload */}
        <div className={styles.formSection}>
          <label className={styles.label}>Channel Logo (200x200px)</label>
          <div className={styles.logoUpload}>
            <div
              className={styles.logoPreview}
              style={{
                backgroundImage: previewLogo ? `url(${previewLogo})` : 'none',
                backgroundColor: formData.themeColor
              }}
            >
              {!previewLogo && (
                <div className={styles.logoPlaceholder}>
                  {formData.name.charAt(0) || 'C'}
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileChange(e, "logo")}
                className={styles.fileInput}
              />
            </div>
          </div>
        </div>

        {/* Basic Info */}
        <div className={styles.formSection}>
          <label className={styles.label}>Channel Name *</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Enter channel name"
            maxLength="50"
            required
            className={styles.input}
          />
          <div className={styles.charCount}>{formData.name.length}/50</div>
        </div>

        <div className={styles.formSection}>
          <label className={styles.label}>Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Describe your channel..."
            rows="3"
            maxLength="500"
            className={styles.textarea}
          />
          <div className={styles.charCount}>{formData.description.length}/500</div>
        </div>

        {/* Category & Privacy */}
        <div className={styles.formRow}>
          <div className={styles.formSection}>
            <label className={styles.label}>Category</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className={styles.select}
            >
              {categories.map(cat => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.formSection}>
            <label className={styles.label}>Privacy</label>
            <select
              value={formData.privacy}
              onChange={(e) => setFormData({ ...formData, privacy: e.target.value })}
              className={styles.select}
            >
              <option value="public">🌐 Public - Anyone can join</option>
              <option value="private">🔒 Private - By invitation only</option>
            </select>
          </div>
        </div>

        {/* Theme Color */}
        <div className={styles.formSection}>
          <label className={styles.label}>Theme Color</label>
          <div className={styles.colorPicker}>
            <input
              type="color"
              value={formData.themeColor}
              onChange={(e) => setFormData({ ...formData, themeColor: e.target.value })}
            />
            <span className={styles.colorValue}>{formData.themeColor}</span>
          </div>
        </div>

        {/* Rules */}
        <div className={styles.formSection}>
          <label className={styles.label}>Channel Rules (one per line)</label>
          <textarea
            value={formData.rules}
            onChange={(e) => setFormData({ ...formData, rules: e.target.value })}
            placeholder="Rule 1&#10;Rule 2&#10;Rule 3"
            rows="4"
            className={styles.textarea}
          />
        </div>

        {/* Welcome Message */}
        <div className={styles.formSection}>
          <label className={styles.label}>Welcome Message (sent to new members)</label>
          <textarea
            value={formData.welcomeMessage}
            onChange={(e) => setFormData({ ...formData, welcomeMessage: e.target.value })}
            placeholder="Welcome message..."
            rows="3"
            maxLength="200"
            className={styles.textarea}
          />
        </div>

        {/* Submit Button */}
        <div className={styles.formActions}>
          <button
            type="button"
            onClick={() => navigate('/clubs')}
            className={styles.btnSecondary}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || !formData.name.trim()}
            className={styles.submitBtn}
          >
            {loading ? "Creating..." : "Create Channel"}
          </button>
        </div>
      </form>
    </div>
  );
}